// src/features/surveys/MonthlySurveyCard.js
// Small card on the learner dashboard inviting the learner to the monthly
// feedback survey. Shows only while the survey is open (25th → 7th of next
// month), only if the learner hasn't answered yet, and not while snoozed.
//
// Testing: open the dashboard with ?survey=preview in the address bar to see
// the card any day. Preview answers are saved under "Test responses" in the
// admin Feedback tab, where they can be deleted.
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import SurveyRunner from './SurveyRunner';
import {
  MONTHLY_SURVEY,
  MONTHLY_SNOOZE_DAYS,
  MONTHLY_MAX_SNOOZES,
  PREVIEW_PERIOD,
  hasLanguage,
  getMonthlySurveyPeriod,
  monthlyResponseId
} from './surveyDefinitions';

const TEAL = '#19a4a0';

function isPreviewMode() {
  try {
    return new URLSearchParams(window.location.search).get('survey') === 'preview';
  } catch {
    return false;
  }
}

function MonthlySurveyCard({ userId, userName, userEmail, cohortId, groups = [] }) {
  const preview = isPreviewMode();
  const period = preview ? PREVIEW_PERIOD : getMonthlySurveyPeriod(new Date());

  const [checking, setChecking] = useState(true);
  const [visible, setVisible] = useState(false);
  const [snoozeCount, setSnoozeCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId || !period) { setChecking(false); return; }
    let cancelled = false;

    const check = async () => {
      try {
        const [responseSnap, stateSnap] = await Promise.all([
          getDoc(doc(db, 'survey_responses', monthlyResponseId(period, userId))),
          getDoc(doc(db, 'survey_state', userId))
        ]);
        if (cancelled) return;

        if (responseSnap.exists()) { setVisible(false); return; }

        const monthly = stateSnap.exists() ? stateSnap.data().monthly : null;
        const samePeriod = monthly && monthly.period === period;
        const count = samePeriod ? (monthly.snoozeCount || 0) : 0;
        const snoozedUntil = samePeriod && monthly.snoozedUntil?.toDate ? monthly.snoozedUntil.toDate() : null;
        setSnoozeCount(count);

        if (preview) { setVisible(true); return; }
        if (count >= MONTHLY_MAX_SNOOZES) { setVisible(false); return; }
        if (snoozedUntil && snoozedUntil > new Date()) { setVisible(false); return; }
        setVisible(true);
      } catch (e) {
        // If anything fails, quietly don't show the card — never block the dashboard.
        console.error('Monthly survey check failed:', e);
        if (!cancelled) setVisible(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [userId, period, preview]);

  const handleLater = async () => {
    setVisible(false);
    if (preview) return;
    try {
      await setDoc(doc(db, 'survey_state', userId), {
        monthly: {
          period,
          snoozeCount: snoozeCount + 1,
          snoozedUntil: Timestamp.fromMillis(Date.now() + MONTHLY_SNOOZE_DAYS * 24 * 60 * 60 * 1000)
        }
      }, { merge: true });
    } catch (e) {
      console.error('Could not save survey snooze:', e);
    }
  };

  const handleSubmit = async (answers, language) => {
    await setDoc(doc(db, 'survey_responses', monthlyResponseId(period, userId)), {
      surveyId: 'monthly',
      period,
      learnerId: userId,
      learnerName: userName || '',
      learnerEmail: userEmail || '', // login email — unique, used to tell learners apart
      cohortId: cohortId || null,
      groupIds: groups.map(g => g.id),
      groupNames: groups.map(g => g.name || ''),
      tutorIds: [...new Set(groups.map(g => g.tutor_id).filter(Boolean))],
      answers,
      language,
      submittedAt: serverTimestamp()
    });
  };

  const handleClose = () => {
    setOpen(false);
    // Re-check: if they submitted, the card disappears; if they just closed, it stays.
    getDoc(doc(db, 'survey_responses', monthlyResponseId(period, userId)))
      .then(snap => { if (snap.exists()) setVisible(false); })
      .catch(() => {});
  };

  if (checking || !visible) return null;

  // Before the learner picks a language, the card shows Hindi under English.
  const showHindi = hasLanguage(MONTHLY_SURVEY, 'hi');
  const t = MONTHLY_SURVEY;

  return (
    <>
      <div style={styles.card}>
        <div style={styles.icon}>💬</div>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={styles.title}>
            {t.title.en}
            {showHindi && <span style={{ fontWeight: 600 }}>· {t.title.hi}</span>}
            {preview && <span style={styles.previewTag}>TEST</span>}
          </div>
          <div style={styles.text}>{t.card.text.en}</div>
          {showHindi && <div style={{ ...styles.text, marginTop: '2px' }}>{t.card.text.hi}</div>}
        </div>
        <div style={styles.actions}>
          <button onClick={() => setOpen(true)} style={styles.primaryBtn}>
            {t.startLabel.en}{showHindi && ` · ${t.startLabel.hi}`}
          </button>
          <button onClick={handleLater} style={styles.laterBtn}>
            {t.card.later.en}{showHindi && ` · ${t.card.later.hi}`}
          </button>
        </div>
      </div>

      {open && (
        <SurveyRunner
          survey={MONTHLY_SURVEY}
          learnerName={userName}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />
      )}
    </>
  );
}

const styles = {
  card: {
    display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
    background: '#f0fdfa', border: `1px solid ${TEAL}`, borderRadius: '12px',
    padding: '14px 18px', margin: '0 0 1.5rem'
  },
  icon: { fontSize: '28px', flexShrink: 0 },
  title: { fontWeight: 700, color: '#0f766e', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px' },
  text: { fontSize: '0.9rem', color: '#374151', lineHeight: 1.4 },
  actions: { display: 'flex', gap: '8px', flexShrink: 0 },
  primaryBtn: {
    background: TEAL, color: 'white', border: 'none', borderRadius: '8px',
    padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem'
  },
  laterBtn: {
    background: 'transparent', color: '#6b7280', border: 'none',
    padding: '8px 10px', cursor: 'pointer', fontSize: '0.9rem'
  },
  previewTag: {
    fontSize: '0.7rem', background: '#e8a327', color: 'white', borderRadius: '4px', padding: '1px 6px'
  }
};

export default MonthlySurveyCard;